import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  Link as LinkIcon,
  Users,
  Store,
  ArrowLeftRight,
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { useAuth } from '../context/AuthContext';
import walletService from '../services/walletService';
import NotificationTest from '../components/NotificationTest';
import NotificationTestUpdated from '../components/NotificationTestUpdated';
import MerchantRegistrationTest from '../components/MerchantRegistrationTest';
import MerchantRegistrationSimpleTest from '../components/MerchantRegistrationSimpleTest';
import MerchantRegistrationDebugTest from '../components/MerchantRegistrationDebugTest';
import MerchantRegistrationOffersTest from '../components/MerchantRegistrationOffersTest';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ShimmerStats, ShimmerCard } from '../components/common/Shimmer';
import { cn } from '@/lib/utils';

const formatNumber = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('en-US').format(n);
};

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  if (Math.abs(n) >= 1_000_000) {
    return `SAR ${(n / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(n) >= 1_000) {
    return `SAR ${(n / 1_000).toFixed(1)}K`;
  }
  return `SAR ${n.toFixed(2)}`;
};

const isNegativeChange = (change) => typeof change === 'string' && change.trim().startsWith('-');

const formatRelativeTime = (isoDate, t) => {
  if (!isoDate) return '';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('justNow');
  if (minutes < 60) return t('minutesAgo').replace('{n}', String(minutes));
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('hoursAgo').replace('{n}', String(hours));
  const days = Math.floor(hours / 24);
  return t('daysAgo').replace('{n}', String(days));
};

const formatChartLabel = (dateKey, language) => {
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState({
    users: 0,
    merchants: 0,
    transactions: 0,
    balance: 0,
    changes: {
      users: '0%',
      merchants: '0%',
      transactions: '0%',
      balance: '0%',
    },
    recentActivity: [],
    chart: [],
  });

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await walletService.getDashboardStats(token);
        if (cancelled) return;

        setStatsData({
          users: data.users ?? data.totalUsers ?? 0,
          merchants: data.merchants ?? data.totalMerchants ?? 0,
          transactions: data.totalTransactions ?? 0,
          balance: data.totalBalance ?? 0,
          changes: {
            users: data.changes?.users || '0%',
            merchants: data.changes?.merchants || '0%',
            transactions: data.changes?.transactions || '0%',
            balance: data.changes?.balance || '0%',
          },
          recentActivity: Array.isArray(data.recentActivity) ? data.recentActivity : [],
          chart: Array.isArray(data.chart) ? data.chart : [],
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load dashboard stats');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const stats = [
    {
      key: 'users',
      label: t('users'),
      value: formatNumber(statsData.users),
      change: statsData.changes.users,
      icon: Users,
      accent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      changeClass: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
    },
    {
      key: 'merchants',
      label: t('merchants'),
      value: formatNumber(statsData.merchants),
      change: statsData.changes.merchants,
      icon: Store,
      accent: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      changeClass: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    },
    {
      key: 'transactions',
      label: t('transactions'),
      value: formatNumber(statsData.transactions),
      change: statsData.changes.transactions,
      icon: ArrowLeftRight,
      accent: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
      changeClass: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
    },
    {
      key: 'balance',
      label: t('balance'),
      value: formatCurrency(statsData.balance),
      change: statsData.changes.balance,
      icon: Wallet,
      accent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      changeClass: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
    },
  ];

  const chartData = useMemo(
    () =>
      (statsData.chart || []).map((point) => ({
        ...point,
        label: formatChartLabel(point.date, language),
      })),
    [statsData.chart, language]
  );

  const dailyTxCategories = useMemo(
    () => chartData.map((point) => point.label),
    [chartData]
  );

  const dailyTxSeries = useMemo(
    () => [
      {
        name: t('transactionCount'),
        data: chartData.map((point) => Number(point.transactions || 0)),
      },
    ],
    [chartData, t]
  );

  const dailyTxOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        fontFamily: 'inherit',
        toolbar: { show: false },
        animations: { enabled: true, speed: 700 },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '55%',
          distributed: false,
        },
      },
      colors: ['#7c3aed'],
      dataLabels: { enabled: false },
      stroke: { show: false },
      grid: {
        borderColor: 'hsl(var(--border))',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: dailyTxCategories,
        labels: { style: { fontSize: '11px' }, rotate: -35 },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { fontSize: '11px' },
          formatter: (val) => `${Math.round(val)}`,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${formatNumber(val)}`,
        },
      },
      legend: { show: false },
    }),
    [dailyTxCategories]
  );

  return (
    <div className="max-w-full space-y-6 overflow-x-hidden">
      {loading ? (
        <ShimmerStats count={4} />
      ) : error ? (
        <Card className="border-destructive/40">
          <CardContent className="p-5 text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const down = isNegativeChange(stat.change);
            const TrendIcon = down ? TrendingDown : TrendingUp;
            return (
              <Card key={stat.key} className="overflow-hidden shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                        stat.accent
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'gap-1 border-0 font-medium',
                        down ? 'bg-destructive/10 text-destructive' : stat.changeClass
                      )}
                    >
                      <TrendIcon className="h-3 w-3" />
                      {stat.change}
                    </Badge>
                  </div>

                  <div className="mt-4 space-y-1 text-start">
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      {stat.value}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <Card className="shadow-sm xl:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('activityChartTitle')}</CardTitle>
            <CardDescription>{t('activityChartSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-2">
            {loading ? (
              <ShimmerCard />
            ) : (
              <>
                <div className="h-[300px] w-full" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                        width={48}
                        tickFormatter={(value) =>
                          value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                        }
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name === 'credits' ? t('credits') : t('debits'),
                        ]}
                        labelFormatter={(label) => label}
                        contentStyle={{
                          borderRadius: '8px',
                          border: '1px solid hsl(var(--border))',
                          background: 'hsl(var(--card))',
                          color: 'hsl(var(--foreground))',
                        }}
                      />
                      <Legend
                        formatter={(value) => (value === 'credits' ? t('credits') : t('debits'))}
                      />
                      <Line
                        type="monotone"
                        dataKey="credits"
                        name="credits"
                        stroke="hsl(142 76% 36%)"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="debits"
                        name="debits"
                        stroke="hsl(221 83% 53%)"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="text-start">
                    <h3 className="text-base font-semibold text-foreground">
                      {t('dailyTxChartTitle')}
                    </h3>
                    <p className="text-sm text-muted-foreground">{t('dailyTxChartSubtitle')}</p>
                  </div>
                  <div className="w-full" dir="ltr">
                    <Chart
                      type="bar"
                      height={260}
                      series={dailyTxSeries}
                      options={dailyTxOptions}
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm xl:col-span-2">
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('recentActivity')}</CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {loading ? (
              <div className="p-5">
                <ShimmerCard />
              </div>
            ) : statsData.recentActivity.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {t('noRecentActivity')}
              </div>
            ) : (
              statsData.recentActivity.map((item) => {
                const isCredit = item.type === 'credit';
                return (
                  <div key={item.id} className="flex items-center gap-3 px-5 py-4">
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                        isCredit
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : 'bg-blue-500/10 text-blue-600'
                      )}
                    >
                      {isCredit ? (
                        <ArrowDownLeft className="h-4 w-4" />
                      ) : (
                        <ArrowUpRight className="h-4 w-4" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 text-start">
                      <p className="truncate text-sm font-medium text-foreground">
                        {isCredit ? t('credit') : t('debit')}
                        {item.category ? ` · ${item.category}` : ''}
                      </p>
                      <p className="truncate text-xs text-muted-foreground sm:text-sm">
                        {formatCurrency(item.amount)}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground sm:text-sm">
                      {formatRelativeTime(item.createdAt, t)}
                    </span>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <LinkIcon className="h-4 w-4" />
            {t('publicLinks')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <a href="/merchant-registration" target="_blank" rel="noopener noreferrer">
              {t('merchantRegistration')}
            </a>
          </Button>
        </CardContent>
      </Card>

      {typeof window !== 'undefined' && window.innerWidth >= 1024 && (
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Test Components</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            <MerchantRegistrationDebugTest />
            <MerchantRegistrationSimpleTest />
            <MerchantRegistrationOffersTest />
            <MerchantRegistrationTest />
            <NotificationTest />
            <NotificationTestUpdated />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
